import { Link, useParams } from "react-router-dom";
import { useSeo } from "../lib/seo";
import { PROJECTS } from "../data/portfolio";
import { Frame, Lines, Reveal } from "../components/motion";
import { Eyebrow, Ph } from "../components/ui-bits";
import { ContactCta } from "../components/ContactCta";
import NotFound from "./NotFound";

const Field = ({ label, children }) => (
  <div className="border-t border-ink/15 py-4">
    <dt className="eyebrow">{label}</dt>
    <dd className="mt-2 text-base">{children}</dd>
  </div>
);

const CASE_STEPS = [
  ["partenza", "Il punto di partenza"],
  ["obiettivo", "L'obiettivo"],
  ["strategia", "La strategia"],
  ["contenuti", "I contenuti realizzati"],
  ["risultati", "I risultati"],
];

const Txt = ({ v }) => (v?.startsWith("[") ? <Ph>{v}</Ph> : v);

export default function Progetto() {
  const { slug } = useParams();
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const p = PROJECTS[idx];
  useSeo(p ? `${p.title}${p.place ? ` ${p.place}` : ""}: progetto di comunicazione` : "Progetto non trovato", p ? `${p.title}: ${p.tagline} ${p.tags.join(", ")}. Un progetto di B Digital Studio.` : "Progetto non trovato.");
  if (!p) return <NotFound />;
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <>
      <section className="px-5 pb-14 pt-36 sm:px-10 md:pt-48 lg:px-16" data-testid="project-detail">
        <Reveal delay={0.5}>
          <Link to="/progetti" className="link-u text-sm text-ink/70" data-testid="project-back">← Tutti i progetti</Link>
        </Reveal>
        <div className="mt-10 grid grid-cols-12 gap-y-10 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-8">
            <Reveal delay={0.5}><Eyebrow>{p.caseStudy ? "Case study" : "Progetto"}</Eyebrow></Reveal>
            <Lines as="h1" onLoad delay={0.6} lines={[p.title]} className="mt-6 text-5xl leading-[1.05] sm:text-6xl lg:text-8xl" />
            <Reveal delay={0.8}><p className="mt-6 max-w-2xl font-serif text-2xl leading-snug text-teal md:text-3xl">{p.tagline}</p></Reveal>
          </div>
          <Reveal delay={0.8} className="col-span-12 lg:col-span-3 lg:col-start-10 lg:self-end">
            <dl data-testid="project-meta">
              <Field label="Settore">{p.sector}</Field>
              {p.place && <Field label="Luogo">{p.place}</Field>}
              <Field label="Competenze">{p.tags.join(" · ")}</Field>
            </dl>
          </Reveal>
        </div>
      </section>
      <Frame src={p.cover} alt={`${p.title}: immagine indicativa`} className="mx-5 aspect-[4/5] sm:mx-10 sm:aspect-[16/9] lg:mx-16" priority parallax={6} />
      <p className="mx-5 mt-3 text-xs text-ink/60 sm:mx-10 lg:mx-16">Riferimenti visivi indicativi, non immagini originali del cliente.</p>
      <section className="px-5 py-24 sm:px-10 md:py-32 lg:px-16">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10">
          <Reveal className="col-span-12 md:col-span-4"><Eyebrow n="01">Il progetto</Eyebrow></Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed text-ink/85 md:text-xl" data-testid="project-text">{p.text}</p>
            {p.more?.map((t, i) => (
              <p key={i} className="mt-6 text-lg leading-relaxed text-ink/85 md:text-xl" data-testid={`project-more-${i}`}>{t}</p>
            ))}
          </Reveal>
        </div>
        {p.caseStudy && (
          <div className="mt-20 border-t border-ink/15 md:mt-28" data-testid="case-study">
            {CASE_STEPS.map(([k, label], i) => (
              <Reveal key={k} className="grid grid-cols-12 gap-y-4 border-b border-ink/15 py-10 lg:gap-x-10">
                <p className="col-span-12 font-serif text-sm text-teal md:col-span-1">0{i + 2}</p>
                <h2 className="col-span-12 text-2xl md:col-span-4 md:text-3xl">{label}</h2>
                <p className="col-span-12 text-base leading-relaxed text-ink/80 md:col-span-6 md:col-start-7 md:text-lg"><Txt v={p.caseStudy[k]} /></p>
              </Reveal>
            ))}
          </div>
        )}
        <div className="mt-20 grid grid-cols-12 gap-y-10 md:gap-x-10">
          {p.gallery.map((src, i) => (
            <Frame key={src} src={src} alt={`${p.title}: immagine indicativa ${i + 2}`} className={`col-span-12 ${i % 2 ? "aspect-[4/5] md:col-span-5 md:col-start-8 md:mt-32" : "aspect-[4/5] md:col-span-6"}`} />
          ))}
        </div>
        {p.video && (
          <Reveal className="mt-20">
            <video src={p.video} controls playsInline className="w-full bg-ink" data-testid="project-video" />
          </Reveal>
        )}
      </section>
      <Link to={`/progetti/${next.slug}`} className="group block border-t border-ink/10 px-5 py-20 sm:px-10 md:py-28 lg:px-16" data-testid="project-next">
        <p className="eyebrow">Progetto successivo</p>
        <p className="mt-6 font-serif text-4xl transition-transform duration-700 group-hover:translate-x-4 md:text-7xl">{next.title} →</p>
      </Link>
      <ContactCta />
    </>
  );
}
